<template>
  <div class="floating-search-button">
    <Button
      v-tooltip.left="tooltipLabel"
      rounded
      class="floating-btn"
      data-testid="floating-search-button"
      :aria-label="t('Buscar no sistema')"
      @click="openSearch"
    >
      <i class="pi pi-search"></i>
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';
import { useKeyboardShortcuts } from '../composables/useKeyboardShortcuts';

const { t } = useI18n();
const { getShortcutLabel } = useKeyboardShortcuts();

// The shortcut glyph is data (platform-dependent), not translatable text — it stays outside t()
// so the API-driven translation lookup can't swallow the interpolation placeholder.
const tooltipLabel = computed((): string => {
  const shortcut = getShortcutLabel({
    key: 'b',
    modifiers: ['ctrl/cmd'],
    action: 'modal.global-search.open',
    description: ''
  });
  return `${t('Buscar no sistema')} (${shortcut})`;
});

const openSearch = (): void => {
  log('FloatingSearchButton', 'openSearch', 'Opening the system-wide search modal');
  eventBus.emit('modal.global-search.open');
};
</script>

<style scoped>
/* Sits on the right side, to the LEFT of the FloatingTicketButton cluster, which
   owns the bottom-right corner. That cluster is [assistant FAB][? SpeedDial] (the old
   sidebar FAB is gone — 26/08/2026), so the search button clears exactly those two with
   the cluster's own 12px gap. Expanding its SpeedDial upward would collide with stacking vertically. */
.floating-search-button {
  position: fixed;
  bottom: 24px;
  right: 160px; /* 24 corner + 56 ? button + 12 gap + 56 assistant FAB + 12 gap */
  z-index: 1000;
}

:deep(.floating-btn) {
  width: 56px !important;
  height: 56px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

:deep(.floating-btn:hover) {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* Responsive positioning for smaller screens */
@media (max-width: 768px) {
  .floating-search-button {
    bottom: 16px;
    right: 136px; /* 16 corner + 48 ? button + 12 gap + 48 assistant FAB + 12 gap */
  }

  :deep(.floating-btn) {
    width: 48px !important;
    height: 48px !important;
  }
}
</style>
